import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DownloadSimple, X, DotsThreeOutlineVertical, ShareNetwork, Plus } from "@phosphor-icons/react";

function getBrowserInfo() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  const isChromeAndroid = /Android/.test(ua) && /Chrome/.test(ua) && !/EdgA|OPR|SamsungBrowser/.test(ua);
  const isStandalone = typeof window !== "undefined" && (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
  return { isIOS, isSafari, isChromeAndroid, isStandalone };
}

/**
 * Floating install helper. On the consumer path (`/`, `/hotels` etc) it reacts
 * to `beforeinstallprompt` for a one-tap install. On the admin path (`/admin*`)
 * we ALWAYS show a helper because Chrome frequently suppresses the auto
 * `beforeinstallprompt` for a second PWA on the same origin — the user has to
 * open the browser ⋮ menu manually. Instructions render explicitly for both
 * Android Chrome and iOS Safari.
 */
export default function InstallAppButton() {
  const location = useLocation();
  const isAdmin =
    (typeof window !== "undefined" && window.location.hostname.startsWith("admin.")) ||
    location.pathname.startsWith("/admin");
  const cacheKey = isAdmin ? "zyro_admin_pwa_dismissed" : "zyro_pwa_dismissed";
  const installedKey = isAdmin ? "zyro_admin_pwa_installed" : "zyro_pwa_installed";

  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(cacheKey) === "1"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    setDismissed(localStorage.getItem(cacheKey) === "1");
    setDeferred(null);
    setManualOpen(false);
    setVisible(false);
  }, [cacheKey]);

  useEffect(() => {
    if (dismissed) return;
    const { isStandalone } = getBrowserInfo();
    if (isStandalone) return; // already running as installed PWA

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem(installedKey, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // On the admin path, Chrome will usually NOT fire beforeinstallprompt
    // again after the consumer PWA has been installed. So we force-show the
    // helper after a short delay so the user always sees the install path.
    let forceTimer = null;
    if (isAdmin) {
      forceTimer = setTimeout(() => setVisible(true), 1500);
    } else {
      // Consumer side: iOS Safari never fires beforeinstallprompt — show hint.
      const { isIOS, isSafari } = getBrowserInfo();
      if (isIOS && isSafari) {
        forceTimer = setTimeout(() => setVisible(true), 2500);
      }
    }
    return () => {
      if (forceTimer) clearTimeout(forceTimer);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismissed, isAdmin, installedKey]);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      setVisible(false);
      if (choice?.outcome === "accepted") {
        localStorage.setItem(installedKey, "1");
      } else {
        localStorage.setItem(cacheKey, "1");
        setDismissed(true);
      }
    } else {
      // No native prompt available (typical for the 2nd PWA on same origin,
      // or iOS Safari). Show manual instructions.
      setManualOpen(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem(cacheKey, "1");
    setDismissed(true);
    setVisible(false);
    setManualOpen(false);
  };

  if (!visible || dismissed) return null;

  const label = isAdmin ? "Install Zyro Admin" : "Install Zyro app";
  const desc = isAdmin
    ? "Get its own icon on your home screen"
    : "Fast access, offline home page & shortcuts";
  const bgClass = isAdmin ? "bg-[#0b1220]" : "bg-midnight";

  return (
    <>
      <div
        data-testid={isAdmin ? "pwa-install-banner-admin" : "pwa-install-banner"}
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-[92%] rounded-2xl text-white shadow-2xl border border-white/10 backdrop-blur-lg ${bgClass}`}
      >
        <div className="flex items-center gap-3 p-4">
          <img src="/icon-192.png" alt="Zyro" className="w-11 h-11 rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight">{label}</div>
            <div className="text-xs text-white/70 leading-tight mt-0.5">{desc}</div>
          </div>
          <button
            onClick={install}
            data-testid={isAdmin ? "pwa-install-btn-admin" : "pwa-install-btn"}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gold text-midnight text-xs font-semibold hover:brightness-110 transition"
          >
            <DownloadSimple size={16} weight="bold" /> Install
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid={isAdmin ? "pwa-install-dismiss-admin" : "pwa-install-dismiss"}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {manualOpen && <ManualInstallModal isAdmin={isAdmin} onClose={() => setManualOpen(false)} onDismiss={dismiss} />}
    </>
  );
}

function ManualInstallModal({ isAdmin, onClose, onDismiss }) {
  const { isIOS, isSafari, isChromeAndroid } = getBrowserInfo();
  const appName = isAdmin ? "Zyro Admin" : "Zyro";

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4" data-testid="pwa-install-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl overflow-hidden text-white ${isAdmin ? "bg-[#0b1220]" : "bg-midnight"}`}>
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <img src="/icon-192.png" alt={appName} className="w-12 h-12 rounded-xl" />
          <div className="flex-1">
            <h3 className="font-serif text-xl">Install {appName}</h3>
            <p className="text-xs text-white/60">Add {appName} to your home screen</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="pwa-install-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          {isIOS && isSafari ? (
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">1</span>
                <div>Tap the <b>Share</b> button <ShareNetwork className="inline mx-1" size={16} weight="bold" /> at the bottom of Safari.</div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">2</span>
                <div>Scroll and tap <b>Add to Home Screen</b> <Plus className="inline mx-1" size={16} weight="bold" />.</div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">3</span>
                <div>Confirm the name shows as <b>“{appName}”</b> and tap <b>Add</b>.</div>
              </li>
            </ol>
          ) : (
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">1</span>
                <div>
                  Tap the <b>three-dot menu</b> <DotsThreeOutlineVertical className="inline mx-1" size={16} weight="bold" /> at the top-right of Chrome.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">2</span>
                <div>Tap <b>Install app</b> or <b>Add to Home screen</b>.</div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-xs">3</span>
                <div>Confirm the name is <b>“{appName}”</b> and tap <b>Install</b>.</div>
              </li>
            </ol>
          )}

          {isAdmin && (
            <div className="mt-2 p-3 rounded-xl bg-gold/10 border border-gold/30 text-gold text-xs leading-relaxed">
              💡 Chrome sometimes suppresses the auto install prompt for a second app from the same website — this manual method always works. You will get a <b>separate icon</b> named <b>“{appName}”</b> alongside your existing Zyro app.
            </div>
          )}

          {isChromeAndroid && !isAdmin && (
            <p className="text-[11px] text-white/50">Don’t see the option? Ensure Chrome is up to date and you have visited this page for a few seconds.</p>
          )}
        </div>

        <div className="p-4 border-t border-white/10 flex gap-3">
          <button onClick={onDismiss} className="flex-1 py-2.5 text-xs uppercase tracking-widest text-white/60 hover:text-white">
            Not now
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-full bg-gold text-midnight text-xs font-semibold uppercase tracking-widest">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
